import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const PolicyManagement = () => {
  const [policies, setPolicies] = useState([
    {
      id: 1,
      name: 'Terms of Service',
      type: 'terms',
      version: '2.1',
      effectiveDate: '2026-01-15',
      lastUpdated: '2026-01-10',
      status: 'active',
      wordCount: 4523
    },
    {
      id: 2,
      name: 'Privacy Policy',
      type: 'privacy',
      version: '1.8',
      effectiveDate: '2025-12-01',
      lastUpdated: '2025-11-28',
      status: 'active',
      wordCount: 3210
    },
    {
      id: 3,
      name: 'Community Guidelines',
      type: 'community',
      version: '1.4',
      effectiveDate: '2026-02-01',
      lastUpdated: '2026-01-25',
      status: 'scheduled',
      wordCount: 2145
    },
    {
      id: 4,
      name: 'California Regulations',
      type: 'state-specific',
      version: '1.2',
      effectiveDate: '2025-11-15',
      lastUpdated: '2025-11-10',
      status: 'active',
      wordCount: 1876
    }
  ]);

  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  const policyTypeOptions = [
    { value: 'terms', label: 'Terms of Service' },
    { value: 'privacy', label: 'Privacy Policy' },
    { value: 'community', label: 'Community Guidelines' },
    { value: 'state-specific', label: 'State-Specific Regulations' },
    { value: 'cookie', label: 'Cookie Policy' },
    { value: 'refund', label: 'Refund Policy' }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-success/10', text: 'text-success', icon: 'CheckCircle', label: 'Active' },
      scheduled: { bg: 'bg-warning/10', text: 'text-warning', icon: 'Clock', label: 'Scheduled' },
      draft: { bg: 'bg-muted', text: 'text-muted-foreground', icon: 'FileEdit', label: 'Draft' },
      archived: { bg: 'bg-error/10', text: 'text-error', icon: 'Archive', label: 'Archived' }
    };
    const config = statusConfig?.[status] || statusConfig?.draft;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        <Icon name={config?.icon} size={12} className="mr-1" />
        {config?.label}
      </span>
    );
  };

  const handleCreatePolicy = () => {
    const newPolicy = {
      id: policies?.length + 1,
      name: 'New Policy',
      type: 'draft',
      version: '1.0',
      effectiveDate: new Date()?.toISOString()?.split('T')?.[0],
      lastUpdated: new Date()?.toISOString()?.split('T')?.[0],
      status: 'draft',
      wordCount: 0
    };
    setPolicies([...policies, newPolicy]);
    setSelectedPolicy(newPolicy);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="BookOpen" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground">Policy Management</h2>
              <p className="font-body text-sm text-muted-foreground">Manage platform policies with version control and scheduling</p>
            </div>
          </div>
          <Button iconName="Plus" onClick={handleCreatePolicy}>
            Create New Policy
          </Button>
        </div>
      </div>

      {/* Policy List */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <h3 className="font-heading text-base font-semibold text-foreground mb-4">Active Policies</h3>
        <div className="space-y-3">
          {policies?.map((policy) => (
            <div
              key={policy?.id}
              className="p-4 bg-muted/50 rounded-md border border-border hover:border-primary/50 transition-smooth cursor-pointer"
              onClick={() => setSelectedPolicy(policy)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-body text-base font-semibold text-foreground">{policy?.name}</h4>
                    {getStatusBadge(policy?.status)}
                    <span className="text-xs text-muted-foreground">v{policy?.version}</span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={14} />
                      <span>Effective: {policy?.effectiveDate}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={14} />
                      <span>Updated: {policy?.lastUpdated}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="FileText" size={14} />
                      <span>{policy?.wordCount} words</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm" iconName="Edit">
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" iconName="History">
                    History
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Policy Editor */}
      {selectedPolicy && (
        <div className="bg-card rounded-lg shadow-elevation-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-base font-semibold text-foreground">Edit Policy: {selectedPolicy?.name}</h3>
            <Button variant="outline" size="sm" iconName="X" onClick={() => setSelectedPolicy(null)}>
              Close
            </Button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Policy Name"
                value={selectedPolicy?.name}
                onChange={(e) => setSelectedPolicy({ ...selectedPolicy, name: e?.target?.value })}
                required
              />
              <Select
                label="Policy Type"
                options={policyTypeOptions}
                value={selectedPolicy?.type}
                onChange={(value) => setSelectedPolicy({ ...selectedPolicy, type: value })}
                required
              />
              <Input
                label="Version Number"
                value={selectedPolicy?.version}
                onChange={(e) => setSelectedPolicy({ ...selectedPolicy, version: e?.target?.value })}
                required
              />
              <Input
                label="Effective Date"
                type="date"
                value={selectedPolicy?.effectiveDate}
                onChange={(e) => setSelectedPolicy({ ...selectedPolicy, effectiveDate: e?.target?.value })}
                description="When this policy takes effect"
                required
              />
            </div>
            <div>
              <label className="block font-body text-sm font-medium text-foreground mb-2">
                Policy Content
              </label>
              <textarea
                className="w-full h-64 px-3 py-2 border border-input rounded-md bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Enter policy content here..."
                defaultValue="This is the policy content area. In a production environment, this would be a rich text editor with formatting capabilities."
              />
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" iconName="Eye">
                  Preview
                </Button>
                <Button variant="outline" size="sm" iconName="History" onClick={() => setShowVersionHistory(!showVersionHistory)}>
                  Version History
                </Button>
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm">
                  Save as Draft
                </Button>
                <Button variant="default" size="sm" iconName="Check">
                  Publish
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version History */}
      {showVersionHistory && selectedPolicy && (
        <div className="bg-card rounded-lg shadow-elevation-2 p-6">
          <h3 className="font-heading text-base font-semibold text-foreground mb-4">Version History: {selectedPolicy?.name}</h3>
          <div className="space-y-2">
            {[2.1, 2.0, 1.9, 1.8]?.map((version) => (
              <div key={version} className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                <div className="flex items-center space-x-3">
                  <Icon name="GitBranch" size={16} className="text-muted-foreground" />
                  <div>
                    <p className="font-body text-sm font-medium text-foreground">Version {version}</p>
                    <p className="font-caption text-xs text-muted-foreground">Updated on 2026-01-{10 + Math.floor(version * 2)} by admin@trailerhub.com</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm" iconName="Eye">
                    View
                  </Button>
                  <Button variant="outline" size="sm" iconName="RotateCcw">
                    Restore
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notification Templates */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-md">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Bell" size={18} className="text-primary" strokeWidth={2} />
            <span>Automated Notification Templates</span>
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-card rounded-md border border-border">
              <div>
                <p className="font-body text-sm font-medium text-foreground">Policy Update Notification</p>
                <p className="font-caption text-xs text-muted-foreground">Sent when policies are updated</p>
              </div>
              <Button variant="outline" size="sm" iconName="Edit">
                Edit Template
              </Button>
            </div>
            <div className="flex items-center justify-between p-3 bg-card rounded-md border border-border">
              <div>
                <p className="font-body text-sm font-medium text-foreground">Acceptance Required Email</p>
                <p className="font-caption text-xs text-muted-foreground">Sent when user acceptance is needed</p>
              </div>
              <Button variant="outline" size="sm" iconName="Edit">
                Edit Template
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* State-Specific Regulations */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="p-4 bg-secondary/5 border border-secondary/20 rounded-md">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="MapPin" size={18} className="text-secondary" strokeWidth={2} />
            <span>State-Specific Regulations</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {['California', 'Texas', 'Florida', 'New York']?.map((state) => (
              <div key={state} className="flex items-center justify-between p-3 bg-card rounded-md border border-border">
                <div className="flex items-center space-x-2">
                  <Icon name="FileText" size={16} className="text-muted-foreground" />
                  <span className="font-body text-sm text-foreground">{state} Regulations</span>
                </div>
                <Button variant="outline" size="sm" iconName="ExternalLink">
                  View
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Info" size={16} />
            <span>All policy changes are tracked with full audit trail and rollback capability</span>
          </div>
          <Button variant="outline" size="sm" iconName="Download">
            Export All Policies
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PolicyManagement;